/**
 * Input Validation Utilities
 * Sanitize and validate user inputs for security
 */

export class InputValidator {
  // Email validation
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  }

  // Password validation
  static isValidPassword(password: string): boolean {
    return password.length >= 8 && password.length <= 128;
  }

  // Company name validation
  static isValidCompanyName(name: string): boolean {
    const sanitized = this.sanitizeString(name);
    return sanitized.length >= 2 && sanitized.length <= 100;
  }

  // Contract number validation
  static isValidContractNumber(contractNumber: string): boolean {
    const sanitized = this.sanitizeString(contractNumber);
    return sanitized.length >= 1 && sanitized.length <= 50;
  }

  // General string sanitization
  static sanitizeString(input: string): string {
    if (typeof input !== 'string') return '';
    
    return input
      .trim()
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .substring(0, 1000); // Limit length
  }

  // Number validation
  static isValidNumber(value: any, min?: number, max?: number): boolean {
    const num = Number(value);
    if (isNaN(num)) return false;
    
    if (min !== undefined && num < min) return false;
    if (max !== undefined && num > max) return false;
    
    return true;
  }

  // Year validation
  static isValidYear(year: any): boolean {
    const currentYear = new Date().getFullYear();
    return this.isValidNumber(year, 2000, currentYear + 10);
  }

  // Task data validation
  static isValidTaskData(data: string): boolean {
    const sanitized = this.sanitizeString(data);
    return sanitized.length <= 500;
  }

  // Comments validation
  static isValidComment(comment: string): boolean {
    const sanitized = this.sanitizeString(comment);
    return sanitized.length <= 1000;
  }

  // Invitation code validation
  static isValidInvitationCode(code: string): boolean {
    const sanitized = this.sanitizeString(code);
    return sanitized.length >= 5 && sanitized.length <= 50;
  }

  // Validate all contract fields
  static validateContract(contract: any): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!this.isValidContractNumber(contract.contractNumber)) {
      errors.push('Invalid contract number');
    }

    if (!this.isValidString(contract.product)) {
      errors.push('Invalid product name');
    }

    if (!this.isValidString(contract.transportationVehicle)) {
      errors.push('Invalid transportation vehicle');
    }

    if (!this.isValidYear(contract.year)) {
      errors.push('Invalid year');
    }

    if (!this.isValidString(contract.status)) {
      errors.push('Invalid status');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Generic string validation
  static isValidString(input: string, minLength = 1, maxLength = 100): boolean {
    const sanitized = this.sanitizeString(input);
    return sanitized.length >= minLength && sanitized.length <= maxLength;
  }

  // XSS prevention
  static escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
}

// Export validation functions for easy use
export const {
  isValidEmail,
  isValidPassword,
  isValidCompanyName,
  isValidContractNumber,
  sanitizeString,
  isValidNumber,
  isValidYear,
  isValidTaskData,
  isValidComment,
  isValidInvitationCode,
  validateContract,
  isValidString,
  escapeHtml
} = InputValidator;




